
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { useState } from 'react';
import { IconWithText, ProgressIcon } from '../Icons';
import { LightChip, LightChipProps } from '../LightChip';

export type ChipDropdownOption<T> = {
  value: T;
  title: React.ReactNode;
  subTitle?: React.ReactNode;
  disabledLabel?: string;
  disabled?: boolean;
};

export type ChipDropdownProps<T> = {
  value: T;
  onChange?: (value: T) => Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  options: ChipDropdownOption<T>[];
  testId?: string;
  color?: LightChipProps['color'];
};

export function ChipDropdown<T>({ disabled, loading, value, onChange, options, color, testId = 'chip-dropdown' }: ChipDropdownProps<T>) {
  const { classes } = useStyles();
  const [ open, setOpen ] = useState(false);
  const [ anchor, setAnchor ] = useState<null | HTMLElement>(null);
  const Chevron = open ? ExpandLess : ExpandMore;

  const text = React.useMemo(() => {
    const option = options?.find(option => option.value === value) || options[0];

    if (disabled) {
      return option.disabledLabel ?? option.title;
    }
    return <span>{option.title}</span>;

  }, [ disabled, options, value ]);

  const label = (
    <IconWithText
      placement="end"
      icon={<Chevron className={classes.icon} />}
      textMargin={1.6}
    >
      {text}
    </IconWithText>
  );

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (newValue: T) => {
    if (onChange) {
      onChange(newValue);
    }
    handleClose();
  };

  return (
    <>
      <LightChip
        ref={setAnchor}
        label={label}
        clickable
        avatar={loading ? <Box className={classes.loader}><ProgressIcon /></Box> : undefined}
        disabled={disabled || loading}
        onClick={(disabled || loading) ? undefined : handleClick}
        className={classes.chip}
        testId={testId}
        color={color}
      />
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        keepMounted
        className={classes.menu}
        classes={{
          paper: classes.paper
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {options?.map((option, index) => (
          <SecondaryMenuItem
            key={index}
            selected={value === option.value}
            disabled={option.disabled}
            onClick={() => handleChange(option.value)}
            data-testid={`${testId}-option`}
          >
            <Box width="100%" whiteSpace="wrap">
              <Typography>
                {option.title}
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div">
                {option.subTitle}
              </Typography>
            </Box>
          </SecondaryMenuItem>
        ))}
      </Menu>
    </>
  );
}

const SecondaryMenuItem = withStyles(MenuItem, (theme, _params, classes) => ({
  root: {
    width: '100%',
    [`&.${classes.selected}`]: {
      backgroundColor: theme.palette.secondary.background
    }
  },
  selected: {
  }
}));

const useStyles = makeStyles({ name: 'ChipDropdown' })(theme => ({
  icon: {
    color: theme.palette.text.disabled,
    height: '18px',
    width: '18px',
  },
  loader: {
    padding: theme.spacing(0.5),
    paddingRight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  menu: {
    marginTop: theme.spacing(0.5),
    width: '270px',
  },
  paper: {
    maxWidth: 'none',
    width: '270px',
  },
  chip: {
    height: '24px',
    cursor: 'pointer',
  }
}));
